import React, { useMemo, useState } from 'react';

export interface Abbrev { abbr: string; meaning: string; }

/** Curated, hand-verified common chat abbreviations. Neither sense of ASL here is
 *  American Sign Language: in chat it is the intensifier “as hell” when it follows
 *  an adjective, and the old chat-room question Age, Sex, Location when it stands
 *  alone. Keep this list in step with the static fallback table in the article body
 *  — that table is what crawlers and no-JS readers see. Alphabetised. */
export const ABBREVIATIONS: Abbrev[] = [
  { abbr: 'AFAIK', meaning: 'As Far As I Know' },
  { abbr: 'AFK', meaning: 'Away From Keyboard' },
  { abbr: 'ASAP', meaning: 'As Soon As Possible' },
  { abbr: 'ASL', meaning: 'Age, Sex, Location — or “as hell” after an adjective' },
  { abbr: 'ATM', meaning: 'At The Moment' },
  { abbr: 'B4', meaning: 'Before' },
  { abbr: 'B4N', meaning: 'Bye For Now' },
  { abbr: 'BBL', meaning: 'Be Back Later' },
  { abbr: 'BBS', meaning: 'Be Back Soon' },
  { abbr: 'BFF', meaning: 'Best Friends Forever' },
  { abbr: 'BFN', meaning: 'Bye For Now' },
  { abbr: 'BRB', meaning: 'Be Right Back' },
  { abbr: 'BRT', meaning: 'Be Right There' },
  { abbr: 'BTW', meaning: 'By The Way' },
  { abbr: 'CU', meaning: 'See You' },
  { abbr: 'CYA', meaning: 'See You / See Ya' },
  { abbr: 'DIY', meaning: 'Do It Yourself' },
  { abbr: 'DM', meaning: 'Direct Message' },
  { abbr: 'DND', meaning: 'Do Not Disturb' },
  { abbr: 'EOD', meaning: 'End Of Day' },
  { abbr: 'ETA', meaning: 'Estimated Time of Arrival' },
  { abbr: 'FAQ', meaning: 'Frequently Asked Questions' },
  { abbr: 'FOMO', meaning: 'Fear Of Missing Out' },
  { abbr: 'FTW', meaning: 'For The Win' },
  { abbr: 'FWIW', meaning: 'For What It’s Worth' },
  { abbr: 'FYI', meaning: 'For Your Information' },
  { abbr: 'G2G', meaning: 'Got To Go' },
  { abbr: 'GG', meaning: 'Good Game' },
  { abbr: 'GJ', meaning: 'Good Job' },
  { abbr: 'GL', meaning: 'Good Luck' },
  { abbr: 'GM', meaning: 'Good Morning' },
  { abbr: 'GN', meaning: 'Good Night' },
  { abbr: 'GR8', meaning: 'Great' },
  { abbr: 'GTG', meaning: 'Got To Go' },
  { abbr: 'HBD', meaning: 'Happy Birthday' },
  { abbr: 'HMU', meaning: 'Hit Me Up' },
  { abbr: 'HTH', meaning: 'Hope This Helps' },
  { abbr: 'IANAL', meaning: 'I Am Not A Lawyer' },
  { abbr: 'IDC', meaning: 'I Don’t Care' },
  { abbr: 'IDK', meaning: 'I Don’t Know' },
  { abbr: 'IIRC', meaning: 'If I Recall Correctly' },
  { abbr: 'IKR', meaning: 'I Know, Right?' },
  { abbr: 'ILY', meaning: 'I Love You' },
  { abbr: 'IMO', meaning: 'In My Opinion' },
  { abbr: 'IMHO', meaning: 'In My Humble Opinion' },
  { abbr: 'IRL', meaning: 'In Real Life' },
  { abbr: 'JK', meaning: 'Just Kidding' },
  { abbr: 'K', meaning: 'Okay' },
  { abbr: 'L8R', meaning: 'Later' },
  { abbr: 'LMK', meaning: 'Let Me Know' },
  { abbr: 'LOL', meaning: 'Laughing Out Loud' },
  { abbr: 'MYOB', meaning: 'Mind Your Own Business' },
  { abbr: 'NBD', meaning: 'No Big Deal' },
  { abbr: 'NGL', meaning: 'Not Gonna Lie' },
  { abbr: 'NP', meaning: 'No Problem' },
  { abbr: 'NVM', meaning: 'Never Mind' },
  { abbr: 'OMG', meaning: 'Oh My God' },
  { abbr: 'OMW', meaning: 'On My Way' },
  { abbr: 'OOO', meaning: 'Out Of Office' },
  { abbr: 'OP', meaning: 'Original Poster' },
  { abbr: 'OTW', meaning: 'On The Way' },
  { abbr: 'POV', meaning: 'Point Of View' },
  { abbr: 'PPL', meaning: 'People' },
  { abbr: 'ROFL', meaning: 'Rolling On the Floor Laughing' },
  { abbr: 'SMH', meaning: 'Shaking My Head' },
  { abbr: 'TBH', meaning: 'To Be Honest' },
  { abbr: 'TBT', meaning: 'Throwback Thursday' },
  { abbr: 'TC', meaning: 'Take Care' },
  { abbr: 'TGIF', meaning: 'Thank God It’s Friday' },
  { abbr: 'THX', meaning: 'Thanks' },
  { abbr: 'TIA', meaning: 'Thanks In Advance' },
  { abbr: 'TL;DR', meaning: 'Too Long; Didn’t Read' },
  { abbr: 'TMI', meaning: 'Too Much Information' },
  { abbr: 'TTYL', meaning: 'Talk To You Later' },
  { abbr: 'TY', meaning: 'Thank You' },
  { abbr: 'WBU', meaning: 'What About You' },
  { abbr: 'WFH', meaning: 'Work From Home' },
  { abbr: 'WYD', meaning: 'What You Doing' },
  { abbr: 'YOLO', meaning: 'You Only Live Once' },
  { abbr: 'YW', meaning: 'You’re Welcome' },
];

/** Case-insensitive substring match on abbr OR meaning. Empty/whitespace -> all. */
export function filterAbbrevs(list: Abbrev[], query: string): Abbrev[] {
  const q = query.trim().toLowerCase();
  if (!q) return list;
  return list.filter(
    (a) => a.abbr.toLowerCase().includes(q) || a.meaning.toLowerCase().includes(q),
  );
}

const ChatAbbrevLookup: React.FC = () => {
  const [query, setQuery] = useState('');
  const results = useMemo(() => filterAbbrevs(ABBREVIATIONS, query), [query]);

  return (
    <div className="not-prose my-8 rounded-2xl border border-blue-200 bg-blue-50 p-5 sm:p-6">
      <h3 className="mt-0 mb-3 text-lg font-bold text-gray-900">Chat Abbreviation Lookup</h3>
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search an abbreviation or meaning…"
        aria-label="Search chat abbreviations"
        className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base"
      />
      <div className="mt-2 text-sm text-gray-500">
        {results.length} of {ABBREVIATIONS.length} shown
      </div>
      {results.length === 0 ? (
        <p className="mt-4 mb-0 text-gray-600">No match — try another term.</p>
      ) : (
        <div className="mt-3 max-h-96 overflow-y-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-left text-[16px]">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className="px-4 py-2 font-semibold text-gray-700 w-28">Abbreviation</th>
                <th className="px-4 py-2 font-semibold text-gray-700">Meaning</th>
              </tr>
            </thead>
            <tbody>
              {results.map((a) => (
                <tr key={a.abbr} className="border-t border-gray-100">
                  <td className="px-4 py-2 font-bold text-gray-900">{a.abbr}</td>
                  <td className="px-4 py-2 text-gray-700">{a.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ChatAbbrevLookup;
